import React, { useEffect, useMemo, useRef, useState } from 'react';
import { adminService, AdminUser } from './adminService';
import { Button } from '@/components/ui/button';

const PAGE_SIZE = 20;

const AdminUsersPage = () => {
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await adminService.fetchUsers();
        setUsers(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Ошибка загрузки');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const filteredUsers = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return users;
    return users.filter(u =>
      [u.firstName, u.lastName, u.userName, u.email, String(u.telegramId ?? '')]
        .filter(Boolean)
        .some(v => v!.toLowerCase().includes(q))
    );
  }, [users, search]);

  const totalPages = Math.max(1, Math.ceil(filteredUsers.length / PAGE_SIZE));
  const paginated = filteredUsers.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setPage(1), 0);
  };

  const initials = (u: AdminUser) => {
    const name = [u.firstName, u.lastName].filter(Boolean).join(' ');
    return name ? name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2) : '?';
  };

  const displayName = (u: AdminUser) =>
    [u.firstName, u.lastName].filter(Boolean).join(' ') || '—';

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <div className="h-8 w-48 animate-pulse rounded bg-gray-200" />
            <div className="mt-2 h-4 w-32 animate-pulse rounded bg-gray-100" />
          </div>
        </div>
        <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
          <div className="border-b border-gray-100 p-5">
            <div className="h-5 w-36 animate-pulse rounded bg-gray-200" />
          </div>
          {[...Array(5)].map((_, i) => (
            <div key={i} className="flex items-center gap-4 border-b border-gray-100 px-6 py-4">
              <div className="h-10 w-10 animate-pulse rounded-xl bg-gray-100" />
              <div className="flex-1 space-y-2">
                <div className="h-4 w-40 animate-pulse rounded bg-gray-100" />
                <div className="h-3 w-56 animate-pulse rounded bg-gray-100" />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-gray-900">Пользователи</h1>
          <p className="mt-1 text-sm text-gray-500">Всего: {users.length}</p>
        </div>
      </div>

      {error && (
        <div className="rounded-2xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          {error}
        </div>
      )}

      <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
        {/* Шапка с поиском */}
        <div className="flex flex-col gap-4 border-b border-gray-100 p-5 md:flex-row md:items-center md:justify-between">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Список пользователей</h2>
            <p className="text-sm text-gray-500">
              {filteredUsers.length > 0
                ? `Страница ${page} из ${totalPages} · ${filteredUsers.length} пользователей`
                : 'Нет пользователей'}
            </p>
          </div>
          <div className="w-full md:w-80">
            <input
              type="text"
              placeholder="Поиск по имени, username, email..."
              value={search}
              onChange={handleSearchChange}
              className="w-full rounded-xl border border-gray-200 bg-gray-50 px-4 py-2.5 text-sm text-gray-900 outline-none transition placeholder:text-gray-400 focus:border-gray-400 focus:bg-white"
            />
          </div>
        </div>

        {filteredUsers.length === 0 ? (
          <div className="flex flex-col items-center justify-center px-6 py-14 text-center">
            <div className="flex h-14 w-14 items-center justify-center rounded-full bg-gray-100 text-2xl">
              👤
            </div>
            <h3 className="mt-4 text-lg font-semibold text-gray-900">Никого не найдено</h3>
            <p className="mt-2 max-w-md text-sm text-gray-500">
              Попробуй изменить поисковый запрос.
            </p>
          </div>
        ) : (
          <>
            {/* Мобильные карточки */}
            <div className="grid gap-4 p-4 sm:p-5 lg:hidden">
              {paginated.map(u => (
                <div key={u.id} className="rounded-2xl border border-gray-200 bg-gray-50 p-4">
                  <div className="flex items-start gap-3">
                    <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-gray-900 text-sm font-semibold text-white">
                      {initials(u)}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <p className="text-sm font-semibold text-gray-900">{displayName(u)}</p>
                        {u.adminRole && (
                          <span className="rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700">
                            Админ
                          </span>
                        )}
                      </div>
                      {u.userName && (
                        <p className="mt-1 text-xs text-gray-500">@{u.userName}</p>
                      )}
                      {u.email && (
                        <p className="mt-0.5 text-xs text-gray-500">{u.email}</p>
                      )}
                      {u.telegramId && (
                        <p className="mt-0.5 text-xs text-gray-400">TG: {u.telegramId}</p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Таблица */}
            <div className="hidden overflow-x-auto lg:block">
              <table className="min-w-full">
                <thead className="bg-gray-50">
                  <tr className="border-b border-gray-200">
                    <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                      Пользователь
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                      Username
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                      Email
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                      Telegram ID
                    </th>
                    <th className="px-6 py-4 text-center text-xs font-semibold uppercase tracking-wider text-gray-500">
                      Роль
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {paginated.map(u => (
                    <tr key={u.id} className="transition hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-gray-900 text-sm font-semibold text-white">
                            {initials(u)}
                          </div>
                          <div>
                            <p className="text-sm font-semibold text-gray-900">{displayName(u)}</p>
                            <p className="text-xs text-gray-400">#{u.id}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">
                        {u.userName ? `@${u.userName}` : <span className="text-gray-300">—</span>}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">
                        {u.email || <span className="text-gray-300">—</span>}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">
                        {u.telegramId || <span className="text-gray-300">—</span>}
                      </td>
                      <td className="px-6 py-4 text-center">
                        {u.adminRole ? (
                          <span className="inline-flex items-center rounded-full bg-green-100 px-2.5 py-1 text-xs font-medium text-green-700">
                            Админ
                          </span>
                        ) : (
                          <span className="inline-flex items-center rounded-full bg-gray-100 px-2.5 py-1 text-xs font-medium text-gray-500">
                            Пользователь
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Пагинация */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between border-t border-gray-100 px-5 py-4">
                <p className="text-sm text-gray-500">
                  {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, filteredUsers.length)} из {filteredUsers.length}
                </p>
                <div className="flex items-center gap-1">
                  <Button
                    type="button"
                    onClick={() => setPage(p => p - 1)}
                    disabled={page === 1}
                    variant="outline"
                    className="h-9 w-9 rounded-lg border border-gray-200 p-0 text-gray-600 disabled:opacity-40"
                  >
                    ‹
                  </Button>

                  {Array.from({ length: totalPages }, (_, i) => i + 1)
                    .filter(n => n === 1 || n === totalPages || Math.abs(n - page) <= 1)
                    .reduce<(number | '...')[]>((acc, n, i, arr) => {
                      if (i > 0 && (n as number) - (arr[i - 1] as number) > 1) acc.push('...');
                      acc.push(n);
                      return acc;
                    }, [])
                    .map((item, i) =>
                      item === '...' ? (
                        <span key={`e-${i}`} className="px-1 text-sm text-gray-400">…</span>
                      ) : (
                        <Button
                          key={item}
                          type="button"
                          onClick={() => setPage(item as number)}
                          variant="outline"
                          className={`h-9 w-9 rounded-lg border p-0 text-sm ${
                            page === item
                              ? 'border-gray-900 bg-gray-900 text-white'
                              : 'border-gray-200 text-gray-600 hover:bg-gray-50'
                          }`}
                        >
                          {item}
                        </Button>
                      )
                    )}

                  <Button
                    type="button"
                    onClick={() => setPage(p => p + 1)}
                    disabled={page === totalPages}
                    variant="outline"
                    className="h-9 w-9 rounded-lg border border-gray-200 p-0 text-gray-600 disabled:opacity-40"
                  >
                    ›
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default AdminUsersPage;
